import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { profilePhoto, avatarIllustration, theWhyImage } from "@/assets";

export default function Home() {
  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="relative pt-20 pb-32 overflow-hidden bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            <div className="lg:col-span-7 space-y-8 z-10">
              <h1 className="text-5xl sm:text-6xl md:text-7xl font-serif text-foreground leading-[1.1] tracking-tight">
                Clarity, strategy, and careful execution.
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-2xl">
                A multidisciplinary professional working across data analysis, conversion-focused website design, and app development. I bring analytical rigor and creative intention to every project.
              </p>
              <p className="text-base text-primary font-serif italic">
                "Emerging analyst building through real projects, disciplined study, and applied work."
              </p>
              <div className="flex flex-wrap items-center gap-4 pt-4">
                <Link href="/contact">
                  <Button size="lg" className="bg-primary hover:bg-primary/90 text-white rounded-none px-8 py-6 text-sm uppercase tracking-wider">
                    Work With Me
                  </Button>
                </Link>
                <Link href="/projects">
                  <Button variant="outline" size="lg" className="rounded-none px-8 py-6 text-sm uppercase tracking-wider border-border hover:bg-accent/50 text-foreground">
                    View Projects
                  </Button>
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5 relative">
              <div className="aspect-[3/4] relative overflow-hidden rounded-sm shadow-xl shadow-plum/5">
                <img 
                  src={profilePhoto} 
                  alt="Shateara Davis" 
                  className="object-cover w-full h-full"
                />
                <div className="absolute inset-0 border border-black/5 mix-blend-overlay"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-24 bg-accent/30 border-y border-border/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl text-center">
          <blockquote className="font-serif text-2xl md:text-4xl text-foreground leading-snug text-balance italic">
            "I approach every project as an opportunity to solve real problems. Whether diving into a new dataset or crafting a high-conversion platform, my work is defined by a commitment to clarity, depth, and intentionality."
          </blockquote>
          <p className="mt-8 text-sm uppercase tracking-widest text-muted-foreground font-medium">
            — Shateara Davis
          </p>
        </div>
      </section>

      {/* Who I Work With */}
      <section className="py-20 bg-background border-b border-border/40">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
          <h2 className="text-sm font-semibold uppercase tracking-widest text-primary mb-12">
            Who I Work With
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              "Early Stage Builders",
              "Small Businesses",
              "Mission Driven Brands",
              "Non-profits",
            ].map((group) => (
              <div key={group} className="border border-border/60 p-6 rounded-sm bg-accent/10 hover:bg-accent/30 transition-colors">
                <span className="text-secondary text-lg mr-2">✦</span>
                <span className="font-serif text-lg text-foreground">{group}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Areas of Practice */}
      <section className="py-32 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
          <h2 className="text-sm font-semibold uppercase tracking-widest text-primary mb-16">
            Areas of Practice
          </h2>
          
          <div className="space-y-0">
            {[
              { num: "01", title: "Data Analysis", desc: "Uncovering actionable insights through rigorous statistical foundations, predictive modeling, and clear data visualization." },
              { num: "02", title: "Conversion-Focused Design", desc: "Building trust and driving action with strategic messaging and intentional user experiences." },
              { num: "03", title: "App Development", desc: "Creating functional, thoughtful applications that solve real-world problems elegantly." }
            ].map((service, i) => (
              <div key={i} className="group flex flex-col md:flex-row md:items-baseline gap-4 md:gap-12 py-10 border-t border-border/60 hover:bg-accent/20 transition-colors px-4 -mx-4">
                <div className="text-xl md:text-2xl font-serif text-muted-foreground group-hover:text-primary transition-colors">
                  {service.num}
                </div>
                <div className="flex-1 md:w-1/3">
                  <h3 className="text-2xl md:text-3xl font-serif text-foreground">{service.title}</h3>
                </div>
                <div className="md:w-1/2 text-muted-foreground leading-relaxed">
                  <p>{service.desc}</p>
                </div>
              </div>
            ))}
            <div className="border-t border-border/60 pt-12 text-right">
              <Link href="/services" className="inline-flex items-center gap-2 text-sm uppercase tracking-wider font-semibold hover:text-primary transition-colors group">
                Explore all services 
                <span className="group-hover:translate-x-1 transition-transform">→</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* What I'm building now */}
      <section className="py-32 bg-accent/20 border-t border-border/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
            <h2 className="text-sm font-semibold uppercase tracking-widest text-primary">
              What I'm building now
            </h2>
            <Link href="/projects" className="inline-flex items-center gap-2 text-sm uppercase tracking-wider font-semibold hover:text-primary transition-colors group">
              View all projects 
              <span className="group-hover:translate-x-1 transition-transform">→</span>
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
            <div className="aspect-[4/3] bg-white p-4 shadow-sm border border-border/50 rounded-sm">
              <div className="w-full h-full relative overflow-hidden bg-muted">
                <img 
                  src={theWhyImage} 
                  alt="Sustainable Accountability Systems" 
                  className="object-cover w-full h-full"
                />
              </div>
            </div>
            <div className="space-y-6">
              <div className="inline-block px-3 py-1 bg-amber/10 text-secondary text-xs uppercase tracking-widest font-semibold border border-amber/20">
                In Progress
              </div>
              <h3 className="text-3xl md:text-4xl font-serif text-foreground leading-tight">
                Neurodivergence Accountability App
              </h3>
              <p className="text-lg text-muted-foreground leading-relaxed">
                A tool built around human motivation and behavioral design rather than productivity guilt. Exploring how we can build systems that support consistent effort without burnout.
              </p>
              <Link href="/projects" className="inline-block mt-4 text-primary hover:text-primary/80 font-medium pb-1 border-b border-primary/30 hover:border-primary transition-colors">
                Read the case study
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Editorial split section */}
      <section className="bg-background">
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[80vh]">
          <div className="relative h-[50vh] lg:h-auto overflow-hidden">
            <img 
              src={avatarIllustration} 
              alt="Editorial illustration" 
              className="object-cover w-full h-full object-center"
            />
          </div>
          <div className="flex items-center p-12 lg:p-24 bg-background">
            <div className="max-w-xl space-y-8">
              <h2 className="text-4xl md:text-5xl font-serif text-foreground leading-tight">
                A foundation of steady growth.
              </h2>
              <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
                <p>
                  Meaningful work isn't born from sudden bursts of inspiration. It's the result of disciplined execution, rigorous learning, and a willingness to sit with hard problems until they make sense.
                </p>
                <p>
                  Whether I'm analyzing a dataset to find the signal in the noise, designing a platform optimized for human behavior, or writing about the process, my focus remains constant: clarity, depth, and intentionality.
                </p>
              </div>
              <Link href="/about" className="inline-block pt-4">
                <Button variant="outline" className="rounded-none px-8 py-6 text-sm uppercase tracking-wider border-border hover:bg-accent/50 text-foreground">
                  Read My Story
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Dark plum CTA band */}
      <section className="bg-plum text-white py-24 border-t-4 border-primary">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl text-center space-y-8">
          <h2 className="text-4xl md:text-5xl font-serif text-white leading-tight">
            Ready to build something meaningful.
          </h2>
          <p className="text-lg md:text-xl text-white/80 leading-relaxed max-w-2xl mx-auto font-light">
            If you're looking for a partner who brings both strategic thinking and technical execution to your project, let's start a conversation.
          </p>
          <div className="pt-8">
            <a href="https://calendly.com/shateara-davis-proton" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-secondary hover:bg-secondary/90 text-white rounded-none px-10 py-7 text-sm uppercase tracking-wider font-semibold">
                Book a Consultation
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
